import React, { useMemo, useCallback } from 'react'
import {
	LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts'
import useMediaQuery from '../../store/hooks/useMediaQuery';
import { useTranslation } from 'react-i18next';
import { SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE } from '../../constants/constants';
import { useAppSelector } from '../../store/hooks/hooks';


const PressureDropChart: React.FC = () => {
	const isMobile = useMediaQuery('(max-width: 900px)');
	const data = useAppSelector(state => state.pressureDropCalculator.chartsData);
	const isEmpty = data.q.length === 0;

	const { minX, maxX, minY, maxY, xTicks, yTicks } = useMemo(() => {
		if (isEmpty) {
			const minX = 0, maxX = 100, minY = 0, maxY = 25;
			const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
			const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
			return { minX, maxX, minY, maxY, xTicks, yTicks };
		}
		const minX = Math.min(...data.q);
		const maxX = Math.max(...data.q);
		const minY = Math.min(...data.p, ...data.l);
		const maxY = Math.max(...data.p, ...data.l);
		const xTicks = Array.from({ length: 5 }, (_, i) => minX + (maxX - minX) * i / 4);
		const yTicks = Array.from({ length: 5 }, (_, i) => minY + (maxY - minY) * i / 4);
		return { minX, maxX, minY, maxY, xTicks, yTicks };
	}, [data, isEmpty]);

	const tooltipLabelFormatter = useCallback((value: number) => {
		const numberValue = Number(value);
		return Number.isFinite(numberValue) ? numberValue.toFixed(3) : String(value);
	}, []);

	const tooltipFormatter = useCallback((value: number) => {
		const numberValue = Number(value);
		return Number.isFinite(numberValue) ? [numberValue.toFixed(3)] : [String(value)];
	}, []);

	const { t } = useTranslation("pressure_drop");

	return (
		<div className="pressure-drop__chart round-corner-container">
			<ResponsiveContainer width="100%" height={300} {...SINGLE_RESPONSIVE_CONTAINER_MIN_SIZE}>
				<LineChart data={isEmpty ? [{ q: minX, p: minY, l: minY }] : data.q.map((q, i) => ({ q, p: data.p[i], l: data.l[i] }))} margin={{
					top: 20,
					right: 30,
					left: 30,
					bottom: 40
				}}>
					<CartesianGrid strokeDasharray="0"
						horizontal={true}
						vertical={true}
					/>
					<XAxis
						axisLine={true}
						dataKey="q"
						domain={[minX, maxX]}
						label={{ value: t("chart_labels.q"), position: 'insideBottom', offset: -10 }}
						type="number"
						ticks={xTicks}
						tickFormatter={(value: number) => value.toFixed(0)}
					/>
					<YAxis
						axisLine={true}
						domain={[minY, maxY]}
						ticks={yTicks}
						label={{ value: t("chart_labels.p"), angle: -90, position: 'insideLeft', offset: -17 }}
						tickFormatter={(value: number) => value > 1000 ? value.toExponential(2) : value.toFixed(2)}
					/>
					<Tooltip
						labelFormatter={tooltipLabelFormatter}
						formatter={tooltipFormatter}
						wrapperStyle={{ pointerEvents: 'none' }}
						isAnimationActive={false}
					/>
					<Legend
						formatter={(value: number) => value}
						layout={isMobile ? 'horizontal' : 'vertical'}
						verticalAlign={isMobile ? 'bottom' : 'top'}
						align={isMobile ? 'center' : 'right'}
					/>

					<Line
						type="monotone"
						dataKey="p"
						stroke={isEmpty ? "transparent" : "#4c23deff"}
						strokeWidth={1}
						name={t("chart_labels.line1")}
						dot={false}
						activeDot={false}
						legendType="plainline"
					/>

					<Line
						type="monotone"
						dataKey="l"
						stroke={isEmpty ? "transparent" : "rgb(255, 169, 21)"}
						strokeWidth={1}
						name={t("chart_labels.line_l")}
						dot={false}
						activeDot={false}
						legendType="plainline"
					/>

				</LineChart>
			</ResponsiveContainer>
		</div >
	)
}

export default React.memo(PressureDropChart)